import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../lib/supabase';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState({
    aiApiKey: '',
    aiProvider: 'openai',
    customApiUrl: '',
    localModelId: null,
    theme: 'dark',
    fontSize: 14,
    autoSave: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && !authLoading) {
      loadSettings();
    } else if (!user && !authLoading) {
      // User signed out, load from localStorage or reset to defaults
      loadSettingsFromLocal();
    }
  }, [user, authLoading]);

  const loadSettings = async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log('⚙️ Loading settings from Supabase for user:', user.email);

      const { data, error } = await supabase
        .from('user_settings_ac2025')
        .select('settings')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found, create default
          console.log('📝 No settings found, creating default settings');
          await createDefaultSettings();
        } else {
          throw error;
        }
      } else {
        setSettings(prev => ({ ...prev, ...data.settings }));
        console.log('✅ Settings loaded from Supabase');

        // Merge with any local settings if they exist
        const localSettings = loadSettingsFromLocal(false);
        if (localSettings && Object.keys(localSettings).length > 0) {
          console.log('📱 Found local settings, merging...');
          const mergedSettings = { ...data.settings, ...localSettings };
          await updateSettings(mergedSettings);
          // Clear local storage after successful merge
          localStorage.removeItem('codeEditor_settings');
        }
      }
    } catch (error) {
      console.error('❌ Error loading settings from Supabase:', error);
      // Fallback to localStorage
      loadSettingsFromLocal();
    } finally {
      setLoading(false);
    }
  };

  const createDefaultSettings = async () => {
    if (!user) return;

    const defaultSettings = {
      aiApiKey: '',
      aiProvider: 'openai',
      customApiUrl: '',
      localModelId: null,
      theme: 'dark',
      fontSize: 14,
      autoSave: true
    };

    try {
      const { error } = await supabase
        .from('user_settings_ac2025')
        .insert({
          user_id: user.id,
          settings: defaultSettings
        });

      if (error) throw error;

      setSettings(defaultSettings);
      console.log('✅ Default settings created in Supabase');
    } catch (error) {
      console.error('❌ Error creating default settings:', error);
      setSettings(defaultSettings);
    }
  };

  const loadSettingsFromLocal = (setInState = true) => {
    try {
      const savedSettings = localStorage.getItem('codeEditor_settings');
      if (savedSettings) {
        const localSettings = JSON.parse(savedSettings);
        if (setInState) {
          setSettings(prev => ({ ...prev, ...localSettings }));
          console.log('📱 Settings loaded from localStorage (offline)');
        }
        return localSettings;
      }
    } catch (error) {
      console.error('❌ Error loading settings from localStorage:', error);
    }
    return {};
  };

  const updateSettings = async (newSettings) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);

    // Save to Supabase if user is authenticated
    if (user) {
      try {
        const { error } = await supabase
          .from('user_settings_ac2025')
          .update({ settings: updatedSettings })
          .eq('user_id', user.id);

        if (error) throw error;
        console.log('✅ Settings saved to Supabase');
      } catch (error) {
        console.error('❌ Error saving settings to Supabase:', error);
        // Fallback to localStorage
        saveSettingsToLocal(updatedSettings);
      }
    } else {
      // Save to localStorage if not authenticated
      saveSettingsToLocal(updatedSettings);
    }
  };

  const saveSettingsToLocal = (settingsToSave) => {
    try {
      localStorage.setItem('codeEditor_settings', JSON.stringify(settingsToSave));
      console.log('📱 Settings saved to localStorage (offline)');
    } catch (error) {
      console.error('❌ Error saving settings to localStorage:', error);
    }
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSettings,
      loading
    }}>
      {children}
    </SettingsContext.Provider>
  );
};